import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useDebounce } from 'ahooks'
import { Alert, App, Button, type MenuProps } from 'antd'
import { ChevronDown, ListChecks } from 'lucide-react'
import { z } from 'zod'
import { enterpriseListSchema, type EnterpriseSummary } from '@super-mega/server/contracts/order'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { DEFAULT_PAGE_SEARCH, TABLE_ACTION_COLUMN } from '@/const/list.ts'
import {
  auditedListColumns,
  getAuditFormValues,
  getAuditSearch,
  useMemberCreatorOptions,
  type AuditFormValues
} from '@/features/access/index.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import {
  ENTERPRISE_KINDS,
  EnterpriseBasicsModal,
  EnterpriseCreateModal,
  EnterpriseDetailDrawer,
  EnterpriseDocumentStatus,
  EnterpriseSupplementModal
} from '@/features/service-enterprise/index.ts'
import { usePageTableNavigation } from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import {
  createdAtSearchShape,
  createdAtSortSearchShape,
  creatorMemberSearchShape,
  getPositiveIntegerIdSearchValue
} from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'

const searchSchema = z.object({
  ...createdAtSearchShape,
  ...createdAtSortSearchShape,
  ...creatorMemberSearchShape,
  current: z.coerce.number().int().positive().catch(1),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20),
  id: z.coerce.number().int().positive().optional().catch(undefined),
  keyword: z.string().trim().max(100).optional().catch(undefined),
  customerId: z.coerce.number().int().positive().optional().catch(undefined),
  kind: enterpriseListSchema.shape.kind.catch(undefined),
  documentStatus: enterpriseListSchema.shape.documentStatus.catch(undefined),
  isArchived: z.boolean().optional().catch(undefined)
})
const listQuery = createListQueryPersistence('super-mega:customer-management:subjects:list-query', searchSchema)

export const Route = createFileRoute('/_authenticated/customer-management/subjects')({
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  validateSearch: listQuery.validateSearch,
  component: SubjectsPage
})
type Search = z.infer<typeof searchSchema>
type SearchForm = AuditFormValues & {
  idSearch?: string
  keyword?: string
  customerId?: number
  kind?: EnterpriseSummary['kind']
  documentStatus?: Search['documentStatus']
  archiveStatus?: 'active' | 'archived'
}

function SubjectsPage() {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { modal, message } = App.useApp()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const router = useRouter()
  const [searchBusy, setSearchBusy] = useState(false)
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('subject.') && !permission.endsWith('.read'))
  const canCreate = currentSession.isSuperAdmin || currentSession.permissions.includes('subject.create')
  const memberCreator = useMemberCreatorOptions('subject.read', search.creatorMemberId)
  const [creating, setCreating] = useState(false)
  const [detailId, setDetailId] = useState<number>()
  const [editing, setEditing] = useState<EnterpriseSummary>()
  const [supplementId, setSupplementId] = useState<number>()
  const [selected, setSelected] = useState<EnterpriseSummary[]>([])
  const [customerKeyword, setCustomerKeyword] = useState('')
  const customers = useQuery(
    trpc.lookup.customers.queryOptions({
      purpose: 'subject.read',
      keyword: useDebounce(customerKeyword, { wait: 300 })
    })
  )
  const selectedCustomer = useQuery({
    ...trpc.customer.list.queryOptions({ id: search.customerId, current: 1, pageSize: 1 }),
    enabled: Boolean(search.customerId)
  })
  const customerOptions = [
    ...(selectedCustomer.data?.data ?? []).map((row) => ({ value: row.id, label: row.name })),
    ...(customers.data ?? [])
  ].filter((row, index, rows) => rows.findIndex((other) => other.value === row.value) === index)
  const query = useQuery({ ...trpc.order.subjects.list.queryOptions(search), placeholderData: keepPreviousData })
  const archive = useMutation(trpc.order.subjects.batchSetArchived.mutationOptions())
  const table = usePageTableNavigation<Search, SearchForm>({
    search,
    navigate: navigateSearch,
    defaultSearch: DEFAULT_PAGE_SEARCH,
    showTotal: (total) => `共 ${total} 个服务对象`,
    onQueryChange: () => setSelected([]),
    getFormInitialValues: (value) => {
      let archiveStatus: 'archived' | 'active' | undefined
      if (value.isArchived === true) {
        archiveStatus = 'archived'
      } else if (value.isArchived === false) {
        archiveStatus = 'active'
      }
      return {
        ...getAuditFormValues(value),
        idSearch: value.id?.toString(),
        keyword: value.keyword,
        customerId: value.customerId,
        kind: value.kind,
        documentStatus: value.documentStatus,
        archiveStatus
      }
    },
    getSearchFromForm: (value) => ({
      ...getAuditSearch(value),
      id: getPositiveIntegerIdSearchValue(value.idSearch),
      keyword: value.keyword?.trim() || undefined,
      customerId: value.customerId,
      kind: value.kind,
      documentStatus: value.documentStatus,
      isArchived: value.archiveStatus === undefined ? undefined : value.archiveStatus === 'archived'
    })
  })
  const setArchived = (rows: { id: number; version: number }[], isArchived: boolean) =>
    modal.confirm({
      title: `${isArchived ? '归档' : '恢复'} ${rows.length} 个服务对象？`,
      content: '归档仅影响新单选用，不改变已有订单状态、合同、账单或场地占用。',
      okText: isArchived ? '确认归档' : '确认恢复',
      onOk: async () => {
        try {
          await archive.mutateAsync({
            items: rows.map((row) => ({ subjectId: row.id, expectedVersion: row.version })),
            isArchived
          })
          await Promise.all([
            cache.invalidateQueries(trpc.order.subjects.list.queryFilter()),
            cache.invalidateQueries(trpc.order.subjects.options.queryFilter()),
            cache.invalidateQueries(trpc.order.subjects.detail.queryFilter())
          ])
          setSelected([])
          void message.success(isArchived ? '服务对象已归档' : '服务对象已恢复')
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '操作失败')
          throw error
        }
      }
    })
  const materialsLocation = (ids: number[]) =>
    router.buildLocation({ to: '/customer-management/subjects/materials/batch', search: { ids: ids.join(',') } }).href
  const bySearch = async (action: string) => {
    const tab = action === 'materials' ? window.open('', '_blank') : null
    if (action === 'materials' && !tab) {
      void message.warning('请允许浏览器打开新标签页后重试')
      return
    }
    if (tab) tab.opener = null
    setSearchBusy(true)
    try {
      const result = await cache.fetchQuery({ ...trpc.order.subjects.searchTargets.queryOptions(search), staleTime: 0 })
      if (action === 'materials') {
        if (result.items.length > 20) {
          tab?.close()
          void message.warning('批量补录资料最多支持 20 个对象，请缩小搜索范围')
        } else if (tab && !tab.closed) {
          tab.location.replace(materialsLocation(result.items.map((item) => item.subjectId)))
        }
      } else {
        setArchived(
          result.items.map((item) => ({ id: item.subjectId, version: item.expectedVersion })),
          action === 'archive'
        )
      }
    } catch (cause) {
      tab?.close()
      void message.error(cause instanceof Error ? cause.message : '读取搜索结果失败')
    }
    setSearchBusy(false)
  }
  const batchMenu: MenuProps = {
    items: [
      { key: 'materials', label: '批量补录资料', disabled: selected.length > 20 },
      { key: 'archive', label: '批量归档' },
      { key: 'restore', label: '批量恢复' }
    ],
    onClick: ({ key }) => {
      if (key === 'materials')
        window.open(materialsLocation(selected.map((row) => row.id)), '_blank', 'noopener,noreferrer')
      else setArchived(selected, key === 'archive')
    }
  }
  const columns: ProColumns<EnterpriseSummary>[] = [
    { title: 'ID', dataIndex: 'idSearch', hideInTable: true },
    {
      title: '关键词',
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { maxLength: 100, placeholder: '内部称呼、企业名称、信用代码或法人姓名' }
    },
    {
      title: '所属客户',
      dataIndex: 'customerId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        options: customerOptions,
        loading: customers.isFetching,
        ...proRemoteSearch(setCustomerKeyword)
      }
    },
    {
      title: '资料状态',
      dataIndex: 'documentStatus',
      hideInTable: true,
      valueEnum: {
        missingCompany: '缺企业档案',
        missingIdentity: '缺身份档案',
        missingLicenseOriginal: '缺执照原件',
        missingIdentityOriginal: '缺身份证原件',
        complete: '档案及原件齐全'
      }
    },
    {
      title: '归档状态',
      dataIndex: 'archiveStatus',
      hideInTable: true,
      valueEnum: { active: '未归档', archived: '已归档' }
    },
    { title: 'ID', dataIndex: 'id', search: false, copyable: true, width: '5rem' },
    {
      title: '服务对象',
      dataIndex: 'name',
      search: false,
      width: '14rem',
      render: (_, row) => (
        <Button type="link" className="h-auto! text-left whitespace-normal!" onClick={() => setDetailId(row.id)}>
          {row.name}
          {row.isArchived ? '（已归档）' : ''}
        </Button>
      )
    },
    { title: '内部称呼', dataIndex: 'displayName', search: false, width: '12rem' },
    { title: '所属客户', dataIndex: 'customerName', search: false, width: '12rem' },
    { title: '企业阶段', dataIndex: 'kind', valueEnum: ENTERPRISE_KINDS, width: '9rem' },
    {
      title: '信用代码',
      key: 'creditCode',
      search: false,
      width: '13rem',
      render: (_, row) => row.company?.unifiedSocialCreditCode ?? '—'
    },
    {
      title: '法人 / 拟任法人',
      key: 'legalRepresentative',
      search: false,
      width: '10rem',
      render: (_, row) => row.identity?.name ?? row.company?.legalRepresentative ?? '—'
    },
    {
      title: '档案及原件',
      key: 'documents',
      search: false,
      width: '22rem',
      render: (_, row) => <EnterpriseDocumentStatus status={row.documentStatus} />
    },
    ...auditedListColumns<EnterpriseSummary>({ search, memberCreator }),
    {
      ...TABLE_ACTION_COLUMN,
      render: (_, row) => (
        <TableRowActions
          resource="subject"
          actions={[
            { key: 'view', label: '查看', onClick: () => setDetailId(row.id) },
            ...(canManage
              ? [
                  { key: 'edit', label: '编辑基本信息', onClick: () => setEditing(row) },
                  ...(row.kind !== 'person'
                    ? [{ key: 'supplement', label: '补录执照 / 身份资料', onClick: () => setSupplementId(row.id) }]
                    : [])
                ]
              : []),
            {
              key: 'rentals',
              label: '租赁记录',
              onClick: () =>
                navigate({ to: '/orders/rentals', search: { current: 1, pageSize: 20, subjectId: [row.id] } })
            },
            {
              key: 'services',
              label: '服务记录',
              onClick: () =>
                navigate({ to: '/orders/service', search: { current: 1, pageSize: 20, subjectId: [row.id] } })
            },
            ...(canManage
              ? [
                  {
                    key: 'archive',
                    label: row.isArchived ? '恢复' : '归档',
                    disabled: archive.isPending,
                    onClick: () => {
                      setArchived([row], !row.isArchived)
                    }
                  }
                ]
              : [])
          ]}
        />
      )
    }
  ]
  return (
    <PageContainer
      title="服务对象"
      breadcrumbRender={false}
      content="先确定服务对象，再按办理阶段补齐执照与法人资料；拟设立企业也可先登记。"
    >
      {query.error && (
        <Alert
          className="mb-4"
          type="error"
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      <ProTable<EnterpriseSummary>
        rowKey="id"
        columns={columns}
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        columnsState={{
          persistenceKey: 'customer-management-subjects:table',
          persistenceType: 'localStorage',
          defaultValue: { displayName: { show: false } }
        }}
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        options={{ reload: () => void query.refetch() }}
        scroll={{ x: '110rem' }}
        pagination={{
          ...table.pagination,
          total: query.data?.total ?? 0,
          onChange: (current, pageSize) => {
            setSelected([])
            table.pagination.onChange(current, pageSize)
          }
        }}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_, __, sorter, extra) => {
            if (extra.action !== 'sort') {
              return
            }
            setSelected([])
            const sort = Array.isArray(sorter) ? sorter[0] : sorter
            void navigate({
              replace: true,
              search: (previous) => ({
                ...previous,
                current: 1,
                sortField: 'createdAt',
                sortOrder: sort.order === 'ascend' ? 'asc' : 'desc'
              })
            })
          }
          /* eslint-enable max-params */
        }
        rowSelection={
          canManage
            ? { selectedRowKeys: selected.map((row) => row.id), onChange: (_, rows) => setSelected(rows) }
            : false
        }
        toolBarRender={() => [
          ...(canManage
            ? [
                <AuthorizedDropdown
                  resource="subject"
                  key="batch-actions"
                  menu={batchMenu}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button
                    disabled={selected.length === 0 || archive.isPending}
                    icon={<ListChecks aria-hidden size={16} />}
                  >
                    批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>,
                <AuthorizedDropdown
                  resource="subject"
                  key="search-batch-actions"
                  trigger={['click']}
                  placement="bottomRight"
                  menu={{
                    items: [
                      { key: 'materials', label: '批量补录资料', disabled: (query.data?.total ?? 0) > 20 },
                      { key: 'archive', label: '批量归档' },
                      { key: 'restore', label: '批量恢复' }
                    ],
                    onClick: ({ key }) => void bySearch(key)
                  }}
                >
                  <Button
                    disabled={
                      !query.data?.total ||
                      query.data.total > 100 ||
                      query.isFetching ||
                      archive.isPending ||
                      searchBusy
                    }
                    loading={searchBusy}
                  >
                    按搜索批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>
              ]
            : []),
          ...(canCreate
            ? [
                <Authorized key="create" permissions={['subject.create']}>
                  <Button key="create" type="primary" onClick={() => setCreating(true)}>
                    新建服务对象
                  </Button>
                </Authorized>
              ]
            : [])
        ]}
      />
      <EnterpriseCreateModal open={creating} onOpenChange={setCreating} />
      {detailId && (
        <EnterpriseDetailDrawer
          key={detailId}
          subjectId={detailId}
          onClose={() => setDetailId(undefined)}
          onEdit={
            canManage
              ? () => {
                  const row = query.data?.data.find((item) => item.id === detailId)
                  if (row) {
                    setEditing(row)
                  }
                }
              : undefined
          }
          onSupplement={canManage ? () => setSupplementId(detailId) : undefined}
        />
      )}
      {editing && <EnterpriseBasicsModal initial={editing} onClose={() => setEditing(undefined)} />}
      {supplementId && (
        <EnterpriseSupplementModal
          subjectId={supplementId}
          open
          onOpenChange={(open) => {
            if (!open) {
              setSupplementId(undefined)
            }
          }}
        />
      )}
    </PageContainer>
  )
}
